<template>
  <div class="row">
    <div
      v-for="extension in extensions"
      :key="extension.id"
      class="mb-3 position-relative"
      :class="extension.subOption ? 'col-11 offset-1': 'col-12'"
    >
      <span
        v-if="extension.included"
        class="position-absolute badge badge-pills
        rounded-pill bg-blue-500 text-white fw-bold"
        style="left: 40px; top:-10px; z-index: 2"
      >
        Inclus
      </span>
      <span
        v-else-if="!extension.enabled"
        class="position-absolute badge badge-pills
        rounded-pill bg-yellow-500 text-gray-700 fw-bold"
        style="left: 40px; top:-10px; z-index: 2"
      >
        + {{ getExtensionPrice(extension) }}
      </span>
      <div
        class="card flex-column p-2 shadow"
        :class="{ 'extension-card-readonly': !isExtensionEditable(extension) }"
      >
        <div class="d-flex flex-row">
          <div
            class="d-flex"
            style="z-index: 1"
          >
            <div class="card-body">
              <div class="d-flex flex-row">
                <div class="d-flex flex-column justify-content-around">
                  <div class="lead fw-bold">
                    {{ extension.name }}
                  </div>
                  <div class="mt-3">
                    {{ extension.description }}
                  </div>
                </div>
                <div
                  class="d-flex flex-column ms-auto justify-content-between align-items-end"
                  style="min-width: 80px"
                >
                  <v-switch
                    :active="extension.enabled"
                    :disabled="!isExtensionEditable(extension)"
                    @update="(value) => setWithDebouncer(extension, value)"
                  />
                </div>
              </div>
            </div>
          </div>
          <div
            v-if="showImage"
            class="d-md-flex d-none"
            style="z-index: 0;min-width:255px; width:255px; min-height: 130px"
          >
            <img
              :src="`${aws}/${extension.id}.png`"
              class="img-fluid"
              style="border-radius: 0 15px 15px 0"
            >
          </div>
        </div>
        <div
          v-if="extension.enabled || extension.included"
          class="card-body pt-0"
          :class="{ 'extension-settings-readonly': !isExtensionEditable(extension) }"
        >
          <hr
            v-if="componentType(extension) !== 'v-customisable-extension'
              || (extensions.settings?.length ?? 0) > 0"
          >
          <component
            :is="componentType(extension)"
            :extension="extension"
            :disabled="!isExtensionEditable(extension)"
            @validation="validation = $event"
          />
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { mapGetters } from 'vuex';
import { debounce } from 'lodash-es';
import { ui } from '@/mixins';

import { Switch } from '@/components/common/input';

import Equipment from '@/components/customisation/extensions/Equipment.vue';
import FinancialLoss from '@/components/customisation/extensions/FinancialLoss.vue';
import DynamicExtension from '@/components/customisation/extensions/DynamicExtension.vue';
import CustomisableCyber from '@/components/customisation/extensions/CustomisableCyber.vue';

const perteFinanciereID = 11;
const equipmentID = 29;
const extensionCyberID = 27;
const MARKEL = 39;
const AIG_SME = 10;

const formatter = new Intl.NumberFormat('fr-FR', {
  style: 'currency',
  currency: 'EUR',
  minimumFractionDigits: 2,
});

export default {
  name: 'ContractExtensions',
  components: {
    'v-switch': Switch,
    'v-equipment': Equipment,
    'v-financial-loss': FinancialLoss,
    'v-customisable-extension': DynamicExtension,
    'v-customisable-cyber': CustomisableCyber,
  },
  mixins: [ui],
  data() {
    return {
      debouncer: { cancel: () => {} },
      validation: false,
    };
  },
  computed: {
    ...mapGetters({
      contract: 'contract/get',
    }),
    productId() {
      if (this.contract) {
        return this.contract.productId ?? this.contract.product?.id;
      }
      return null;
    },
    insurerId() {
      if (this.contract) {
        return this.contract.insurerId ?? this.contract.insurer?.id;
      }
      return null;
    },
    isNotFinancialLossCustomModal() {
      return !(this.productId === 16 && this.insurerId === AIG_SME);
    },
    showImage() {
      return (this.contract.insurerId !== AIG_SME || this.contract.productId !== 16)
        && this.contract.insurerId !== MARKEL;
    },
    extensions() {
      if (this.contract) {
        return (this.contract.extensions || [])
          .filter(({ visible }) => visible) || [];
      }
      return [];
    },
    canEditAmendmentSubscription() {
      return this.contract.status === 'AMENDMENT'
        && this.contract.leadStatus !== 'AMENDMENT_VALIDATED'
        && Boolean(this.contract.policyId)
        && !this.contract.activeVersionId
        && this.contract.delegation
        && !this.contract.derogation;
    },
    isDisabled() {
      return !this.canEditAmendmentSubscription;
    },
    aws: () => `${import.meta.env.VITE_AWS_STATIC}/dashboard/png/contracts/extensions`,
  },
  methods: {
    isExtensionEditable(extension) {
      return this.canEditAmendmentSubscription && !extension.included;
    },
    getExtensionPrice({ monthlyPremium }) {
      return formatter.format(monthlyPremium);
    },
    componentType(extension) {
      if (extension.id === extensionCyberID) {
        return 'v-customisable-cyber';
      }
      if (extension.id === equipmentID) {
        return 'v-equipment';
      }
      if (extension.id === perteFinanciereID && this.isNotFinancialLossCustomModal) {
        return 'v-financial-loss';
      }
      return 'v-customisable-extension';
    },
    setCollapseOpen(index, value) {
      this.showList[index] = value;
    },
    setWithDebouncer(extension, isEnabled) {
      if (this.isDisabled || extension.included) {
        return;
      }

      this.debouncer.cancel();
      const toBeCalled = () => {
        this.set(extension, isEnabled);
      };
      this.debouncer = debounce(
        toBeCalled,
        600,
      );
      this.debouncer();
    },
    async set(extension, isEnabled) {
      if (this.isDisabled || extension.included) {
        return;
      }

      const newList = this.extensions.map((elem) => {
        if (elem.id === extension.id) {
          return {
            ...elem,
            enabled: elem.id === extension.id && isEnabled,
          };
        }
        return elem;
      });
      await this.$store.dispatch('contract/updateSubscription', { path: 'extensions', value: newList });
    },
  },
};
</script>
<style scoped>
@media screen and (min-width: 767px) {
  .margin-right-negative {
    margin-right: -15px;
  }
}

.extension-card-readonly {
  background: linear-gradient(90deg, #f8fafc 0%, #eef3f8 100%);
}

.extension-settings-readonly {
  pointer-events: none;
}

.readonly-label {
  color: #708197;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-transform: uppercase;
}
</style>
